import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import {
  Autocomplete,
  CircularProgress,
  TableContainer,
  TablePagination,
  TextField,
} from "@mui/material";
import { Add, RestartAlt } from "@mui/icons-material";
import { getVideos, deleteVideo } from ".";
import { languages } from "../video";
import { StyledTableCell, StyledTableRow } from "../../utils/constantData";
import {
  AdminPageLayout,
  AdminSectionCard,
  TableRowActions,
  DeleteConfirmationDialog,
  AdminAction,
} from "../../components/admin";
import { adminResetButtonSx } from "../../components/admin/adminButtonStyles";
import CategorySubcategoryFields from "../../components/common/CategorySubcategoryFields";
import { useCategorySubcategory } from "../../hooks/useCategorySubcategory";

const formatAddedAt = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const DEFAULT_CATEGORY_NAME = "Agriculture";
const DEFAULT_CATEGORY_ID = 1;
const DEFAULT_LANGUAGE = "Hindi";

export default function VideoList() {
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [video, setVideo] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [lang, setLang] = React.useState(DEFAULT_LANGUAGE);
  const [filterKey, setFilterKey] = React.useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { results } = location.state || {};
  const startIndex = page * rowsPerPage;

  const {
    categories,
    category,
    subCategory,
    subCategories,
    loadingCategories,
    loadingSubCategories,
    subCategoryFieldKey,
    categoryName,
    subCategoryName,
    handleCategoryChange,
    handleSubCategoryChange,
  } = useCategorySubcategory();

  const defaultsAppliedRef = React.useRef(false);
  const [filtersInitialized, setFiltersInitialized] = React.useState(false);
  const [allowEmptyCategory, setAllowEmptyCategory] = React.useState(false);

  const selectedLanguage =
    languages.find((item) => item.name === lang) ||
    languages.find((item) => item.name === DEFAULT_LANGUAGE) ||
    languages[0];

  const isDefaultFilters =
    lang === DEFAULT_LANGUAGE &&
    categoryName === DEFAULT_CATEGORY_NAME &&
    !subCategoryName;

  const hasActiveFilters = !isDefaultFilters;

  const [deleteTarget, setDeleteTarget] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (results) {
      toast.success("Video uploaded successfully!", {
        toastId: "success1",
        position: "bottom-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    }
  }, [results]);

  React.useEffect(() => {
    if (loadingCategories || defaultsAppliedRef.current) return;

    if (categories.length === 0) {
      setFiltersInitialized(true);
      return;
    }

    defaultsAppliedRef.current = true;

    const defaultCategory =
      categories.find((item) => item.id === DEFAULT_CATEGORY_ID) ||
      categories.find((item) => item.name === DEFAULT_CATEGORY_NAME) ||
      categories[0];

    if (defaultCategory) {
      handleCategoryChange(defaultCategory);
    }

    setFiltersInitialized(true);
  }, [categories, loadingCategories, handleCategoryChange]);

  const fetchData = React.useCallback(async () => {
    if (!lang) {
      setVideo([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await getVideos(subCategoryName, lang, categoryName || null, {
        categoryId: category?.id,
        subCategoryId: subCategory?.id,
      });
      const responseData = response.data;
      setVideo(Array.isArray(responseData?.videos) ? responseData.videos : []);
    } catch (error) {
      console.log(error);
      setVideo([]);
    } finally {
      setLoading(false);
    }
  }, [categoryName, subCategoryName, lang, category?.id, subCategory?.id]);

  React.useEffect(() => {
    if (!filtersInitialized || loadingCategories) return;
    if (!categoryName && !allowEmptyCategory) return;
    fetchData();
  }, [fetchData, filtersInitialized, loadingCategories, categoryName, allowEmptyCategory]);

  React.useEffect(() => {
    setPage(0);
  }, [categoryName, subCategoryName, lang]);

  const handleLanguage = (value) => {
    setLang(value?.name || DEFAULT_LANGUAGE);
  };

  const onCategoryChange = (newCategory) => {
    if (!newCategory) {
      setAllowEmptyCategory(true);
    }
    handleCategoryChange(newCategory);
  };

  const handleResetFilters = () => {
    setAllowEmptyCategory(false);
    setLang(DEFAULT_LANGUAGE);
    setPage(0);
    setFilterKey((key) => key + 1);

    const defaultCategory =
      categories.find((item) => item.id === DEFAULT_CATEGORY_ID) ||
      categories.find((item) => item.name === DEFAULT_CATEGORY_NAME) ||
      categories[0];

    if (defaultCategory) {
      handleCategoryChange(defaultCategory);
    } else {
      handleCategoryChange(null);
    }
  };

  const navigateToSendvideo = () => {
    navigate(`/videoUpload/upload`);
  };

  const handleEdit = React.useCallback(
    (videoId) => {
      navigate(`/videoUpload/${videoId}`);
    },
    [navigate]
  );

  const handleDelete = React.useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const result = await deleteVideo(deleteTarget.id);
      if (result) {
        fetchData();
        toast.success("Video Deleted Successfully!", {
          toastId: "success5",
          closeOnClick: true,
          position: "bottom-right",
          hideProgressBar: false,
          pauseOnHover: true,
          autoClose: 1000,
          theme: "light",
          draggable: true,
          progress: undefined,
        });
        setDeleteTarget(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete video", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: true,
        theme: "light",
      });
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, fetchData]);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  return (
    <>
      <AdminPageLayout
        eyebrow="Content Library"
        title="Video Library"
        description="Filter the video library and manage entries from one toolbar."
        actions={
          <div className="admin-toolbar" style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            <Autocomplete
              key={`lang-${filterKey}`}
              disablePortal
              id="language"
              options={languages}
              value={selectedLanguage}
              onChange={(event, newValue) => handleLanguage(newValue)}
              isOptionEqualToValue={(option, value) => option.name === value.name}
              getOptionLabel={(option) => option.name}
              size="small"
              sx={{ minWidth: 160 }}
              renderInput={(params) => <TextField {...params} label="Language" />}
            />
            <CategorySubcategoryFields
              layout="inline"
              categories={categories}
              category={category}
              subCategories={subCategories}
              subCategory={subCategory}
              loadingSubCategories={loadingSubCategories}
              subCategoryFieldKey={subCategoryFieldKey}
              disabled={loadingCategories}
              onCategoryChange={onCategoryChange}
              onSubCategoryChange={handleSubCategoryChange}
              categoryId={`category-${filterKey}`}
              subCategoryId={`subcategory-${filterKey}`}
            />
            <AdminAction
              id="reset-filters-btn"
              variant="outline"
              startIcon={<RestartAlt />}
              disabled={!hasActiveFilters}
              onClick={handleResetFilters}
              sx={{ whiteSpace: "nowrap", ...adminResetButtonSx(hasActiveFilters) }}
            >
              Reset
            </AdminAction>
            <AdminAction
              variant="primary"
              startIcon={<Add />}
              onClick={navigateToSendvideo}
            >
              Upload Video
            </AdminAction>
          </div>
        }
      >
        <AdminSectionCard title="" description="" className="admin-table-shell">
          <TableContainer component="div">
            <Table sx={{ minWidth: 700 }} aria-label="videos table">
              <TableHead>
                <StyledTableCell>Sr. No.</StyledTableCell>
                <StyledTableCell>Title</StyledTableCell>
                <StyledTableCell>Category</StyledTableCell>
                <StyledTableCell width="12%">Sub Category</StyledTableCell>
                <StyledTableCell>Language</StyledTableCell>
                <StyledTableCell>Added On</StyledTableCell>
                <StyledTableCell>Views</StyledTableCell>
                <StyledTableCell align="right" width="20%">Actions</StyledTableCell>
              </TableHead>
              <TableBody>
                {video.length !== 0 ? (
                  video
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => (
                      <StyledTableRow key={row.id || `${row.title}-${index}`}>
                        <StyledTableCell component="th">
                          {startIndex + index + 1}
                        </StyledTableCell>
                        <StyledTableCell>{row.title}</StyledTableCell>
                        <StyledTableCell>{row.category}</StyledTableCell>
                        <StyledTableCell>{row.subCategory}</StyledTableCell>
                        <StyledTableCell>{row.language}</StyledTableCell>
                        <StyledTableCell>{formatAddedAt(row.createdAt)}</StyledTableCell>
                        <StyledTableCell>{row.count}</StyledTableCell>
                        <StyledTableCell align="right">
                          <TableRowActions
                            rowId={row.id}
                            onEdit={() => handleEdit(row.id)}
                            onDelete={() => setDeleteTarget(row)}
                          />
                        </StyledTableCell>
                      </StyledTableRow>
                    ))
                ) : loading ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8">
                      <CircularProgress size={20} className="text-stone-500" />
                      <span className="ml-2 text-stone-500">Loading...</span>
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-stone-500">
                      No Data Found
                    </td>
                  </tr>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={video.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              "& .MuiTablePagination-toolbar": { borderTop: "1px solid #e7e5e4" },
            }}
          />
        </AdminSectionCard>
      </AdminPageLayout>

      <DeleteConfirmationDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Video"
        itemName={deleteTarget?.title}
        loading={deleting}
      />
    </>
  );
}
